import React, { useMemo, useState } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { getApiErrorMessage, useApplySegmentChanges, useDebouncedState, useGetFeatBitSegments, useGetSegmentEntries, useSearchSegmentEntities } from "utils";

import {
  SEGMENT_ENTITY_SEARCH_DEBOUNCE_MS,
  SEGMENT_ENTITY_SEARCH_MIN_LENGTH,
  SEGMENT_ENTITY_SEARCH_PLACEHOLDERS,
  SEGMENT_ENTRY_COLUMNS,
  SEGMENT_TABLE_PAGE_SIZE,
  SEGMENT_UPLOAD_TYPES,
  SEGMENT_UPLOAD_TYPE_LABELS,
  SEGMENT_UPLOAD_TYPE_NOUNS,
} from "consts";
import type { SegmentUploadType } from "consts";

import type { SegmentEntry } from "models";

import {
  AdminHead,
  AdminSection,
  Alert,
  Button,
  Col,
  Flex,
  InputSearch,
  MainPage,
  Modal,
  Option,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  Message as message,
} from "components";

// Several program IDs can share one slug, so programs are matched on the slug the segment stores.
const entryKey = (entry: SegmentEntry) => entry.slug ?? entry.id.toLowerCase();

const countOf = (type: SegmentUploadType, count: number) => `${count.toLocaleString()} ${SEGMENT_UPLOAD_TYPE_NOUNS[type]}${count === 1 ? "" : "s"}`;

// Mentions only what changes, e.g. "2 farms to add and 1 farm to remove".
const describeChanges = (type: SegmentUploadType, adds: number, removes: number) =>
  [adds ? `${countOf(type, adds)} to add` : "", removes ? `${countOf(type, removes)} to remove` : ""].filter(Boolean).join(" and ");

const detailColumns = (type: SegmentUploadType) =>
  SEGMENT_ENTRY_COLUMNS[type].map(({ key, label }, index) => ({
    title: label,
    dataIndex: key,
    key,
    // An ID Klim no longer knows has no details, so its ID is shown in the first column instead.
    render: (value: string | null | undefined, record: object) => {
      const entry = record as SegmentEntry;
      return index === 0 && !entry.inKlim ? `${entry.id} (not found in Klim)` : value ?? "";
    },
  }));

const FeatBitSegmentsEditor = () => {
  const { data: environments, isLoading, error: environmentsError } = useGetFeatBitSegments();
  const { mutateAsync: applyChanges, isPending: isSaving } = useApplySegmentChanges();

  const [envId, setEnvId] = useState<string>();
  const [segmentId, setSegmentId] = useState<string>();
  const [type, setType] = useState<SegmentUploadType>(SEGMENT_UPLOAD_TYPES.USER);
  const [searchText, setSearchText] = useState("");
  const [pendingAdds, setPendingAdds] = useState<SegmentEntry[]>([]);
  const [pendingRemoves, setPendingRemoves] = useState<string[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const segments = environments?.find((environment) => environment.envId === envId)?.segments ?? [];
  // A segment deleted in FeatBit disappears on refetch, so treat it as no longer selected.
  const selectedSegment = segments.find((segment) => segment.id === segmentId);
  const typeLabel = SEGMENT_UPLOAD_TYPE_LABELS[type].toLowerCase();

  const debouncedText = useDebouncedState(searchText.trim(), SEGMENT_ENTITY_SEARCH_DEBOUNCE_MS);
  // An emptied box (e.g. after switching type) stops searching at once instead of after the debounce.
  const debouncedSearch = searchText.trim() ? debouncedText : "";
  const isSearching = debouncedSearch.length >= SEGMENT_ENTITY_SEARCH_MIN_LENGTH;
  const { data: searchResults, isFetching: isFetchingResults, error: searchError } = useSearchSegmentEntities(type, debouncedSearch);
  const { data: segmentList, isFetching: isLoadingList, error: listError } = useGetSegmentEntries({ envId, segmentId: selectedSegment?.id, type });

  const savedByKey = useMemo(() => new Map((segmentList?.entries ?? []).map((entry) => [entryKey(entry), entry])), [segmentList]);
  const savedIds = useMemo(() => new Set((segmentList?.entries ?? []).map((entry) => entry.id)), [segmentList]);
  // Once a save lands, the reloaded list already holds these, so only what is still unsaved counts; this also avoids duplicate rows.
  const unsavedAdds = useMemo(() => pendingAdds.filter((entry) => !savedByKey.has(entryKey(entry))), [pendingAdds, savedByKey]);
  const unsavedRemoves = useMemo(() => pendingRemoves.filter((id) => savedIds.has(id)), [pendingRemoves, savedIds]);
  const pendingAddKeys = useMemo(() => new Set(unsavedAdds.map(entryKey)), [unsavedAdds]);
  const pendingRemoveIds = useMemo(() => new Set(unsavedRemoves), [unsavedRemoves]);

  const savedCount = segmentList?.entries.length ?? 0;
  const resultingCount = savedCount + unsavedAdds.length - unsavedRemoves.length;
  const hasChanges = unsavedAdds.length > 0 || unsavedRemoves.length > 0;

  // Without a search the table shows who is in the segment, including additions not saved yet.
  const rows = useMemo(
    () => (isSearching ? searchResults ?? [] : [...unsavedAdds, ...(segmentList?.entries ?? [])]),
    [isSearching, searchResults, unsavedAdds, segmentList]
  );

  const selectedRowKeys = useMemo(
    () =>
      rows
        .filter((entry) => {
          const saved = savedByKey.get(entryKey(entry));
          return saved ? !pendingRemoveIds.has(saved.id) : pendingAddKeys.has(entryKey(entry));
        })
        .map((entry) => entry.id),
    [rows, savedByKey, pendingRemoveIds, pendingAddKeys]
  );

  const toggle = (entry: SegmentEntry, selected: boolean) => {
    const saved = savedByKey.get(entryKey(entry));
    if (saved) {
      setPendingRemoves((previous) => (selected ? previous.filter((id) => id !== saved.id) : [...new Set([...previous, saved.id])]));
      return;
    }
    setPendingAdds((previous) =>
      selected
        ? [...previous.filter((pending) => entryKey(pending) !== entryKey(entry)), entry]
        : previous.filter((pending) => entryKey(pending) !== entryKey(entry))
    );
  };

  const resetChanges = () => {
    setPendingAdds([]);
    setPendingRemoves([]);
  };

  const changeTarget = (update: () => void) => {
    update();
    resetChanges();
    setSearchText("");
  };

  const handleSave = async () => {
    if (!envId || !selectedSegment) {
      setShowConfirmModal(false);
      return;
    }
    try {
      const report = await applyChanges({
        envId,
        segmentId: selectedSegment.id,
        type,
        add: unsavedAdds.map((entry) => entry.id),
        remove: unsavedRemoves,
      });
      message({ icon: "tick", content: `"${selectedSegment.name}" saved. It now targets ${countOf(type, report.resultingCount)}.` });
      if (report.excluded.length) {
        message({ icon: "error", content: `${countOf(type, report.excluded.length)} not added: they are on the segment's Excluded list in FeatBit.` });
      }
      resetChanges();
    } catch (error) {
      message({ icon: "error", content: `Failed to update segment: ${getApiErrorMessage(error)}` });
    } finally {
      setShowConfirmModal(false);
    }
  };

  const columns = useMemo(
    () => [
      ...detailColumns(type),
      {
        title: "Change",
        key: "change",
        width: "10rem",
        render: (_: unknown, record: object) => {
          const entry = record as SegmentEntry;
          const saved = savedByKey.get(entryKey(entry));
          if (saved && pendingRemoveIds.has(saved.id)) return <Tag colour="red" text="Will be removed" size="small" />;
          if (!saved && pendingAddKeys.has(entryKey(entry))) return <Tag colour="green" text="Will be added" size="small" />;
          return null;
        },
      },
    ],
    [type, savedByKey, pendingRemoveIds, pendingAddKeys]
  );

  const tableError = isSearching ? searchError : listError;

  return (
    <MainPage loading={isLoading} pageName={"admin"}>
      <AdminHead headline="FeatBit Segments" description="Choose which users, farms or carbon programs a FeatBit segment targets." />
      {environmentsError ? (
        <Alert className={undefined} closable={false} type="red" headline="FeatBit is unavailable" description={<>{getApiErrorMessage(environmentsError)}</>} />
      ) : (
        <>
          <AdminSection headline="Target" description="Choose the environment and segment, then the type of entries to manage." />
          <Row className="margin-bottom-200">
            <Col md={8}>
              <Select
                label="Environment"
                tooltip="The FeatBit project and environment to update. Only environments Klim is set up to manage are listed."
                dataTestId="featbit-environment-select"
                value={envId}
                onChange={(value) =>
                  changeTarget(() => {
                    setEnvId(value as string);
                    setSegmentId(undefined);
                  })
                }
              >
                {environments?.map((environment) => (
                  <Option value={environment.envId} key={environment.envId}>
                    {`${environment.projectName} / ${environment.envName}`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col md={8}>
              <Select
                label="Segment"
                tooltip="The FeatBit segment to edit. Create it in FeatBit first, then link it to a flag with 'User is in segment'."
                dataTestId="featbit-segment-select"
                value={selectedSegment?.id}
                onChange={(value) => changeTarget(() => setSegmentId(value as string))}
                disabled={!envId}
                showSearch
                optionFilterProp="children"
                notFoundContent="No segments in this environment"
              >
                {segments.map((segment) => (
                  <Option value={segment.id} key={segment.id}>
                    {segment.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col md={8}>
              <Select
                label="Type"
                tooltip="Users are the segment's members. Farms and carbon programs match anyone whose current farm or program is in the list."
                dataTestId="featbit-type-select"
                value={type}
                onChange={(value) => changeTarget(() => setType(value as SegmentUploadType))}
              >
                {Object.values(SEGMENT_UPLOAD_TYPES).map((uploadType) => (
                  <Option value={uploadType} key={uploadType}>
                    {SEGMENT_UPLOAD_TYPE_LABELS[uploadType]}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {selectedSegment && (
            <>
              <AdminSection
                headline={isSearching ? "Search results" : "Members"}
                description={
                  isSearching
                    ? `${SEGMENT_UPLOAD_TYPE_LABELS[type]} matching "${debouncedSearch}". Ticked ones are in the segment: tick to add, untick to remove.`
                    : `"${selectedSegment.name}" targets ${countOf(type, savedCount)}. Search to add more, or untick to remove.`
                }
              />
              <Row className="margin-bottom-200">
                <Col md={12}>
                  <InputSearch
                    dataTestId="segment-entity-search"
                    placeholder={SEGMENT_ENTITY_SEARCH_PLACEHOLDERS[type]}
                    value={searchText}
                    allowClear
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                </Col>
              </Row>
              <Row className="margin-bottom-200">
                <Col>
                  {tableError ? (
                    <Alert className={undefined} closable={false} type="red" description={<>{getApiErrorMessage(tableError)}</>} />
                  ) : (
                    <Table<SegmentEntry>
                      // A new list starts on its first page.
                      key={`${selectedSegment.id}-${type}-${isSearching ? debouncedSearch : "members"}`}
                      data={rows}
                      columns={columns}
                      rowKey="id"
                      loading={isSearching ? isFetchingResults : isLoadingList}
                      pagination={{ pageSize: SEGMENT_TABLE_PAGE_SIZE }}
                      rowSelection={{
                        selectedRowKeys,
                        onSelect: (record: object, selected: boolean) => toggle(record as SegmentEntry, selected),
                        onSelectAll: (selected: boolean, _selectedRows: object[], changedRows: object[]) =>
                          changedRows.forEach((record) => toggle(record as SegmentEntry, selected)),
                      }}
                      locale={{
                        emptyText: (
                          <Typography variant="paragraph-m" color="grey-600">
                            {isSearching ? `No ${typeLabel} match "${debouncedSearch}".` : `No ${typeLabel} in this segment yet. Search above to add some.`}
                          </Typography>
                        ),
                      }}
                    />
                  )}
                </Col>
              </Row>
              <Flex align="center" justify="space-between" gap="1rem" wrap="wrap" className="margin-bottom-200" data-testid="segment-changes">
                <Typography variant="paragraph-m" color="grey-600">
                  {hasChanges
                    ? `${describeChanges(type, unsavedAdds.length, unsavedRemoves.length)}. After saving, the segment targets ${countOf(type, resultingCount)}.`
                    : "No unsaved changes."}
                </Typography>
                <Flex gap="1rem">
                  <Button type="secondary" onClick={resetChanges} disabled={!hasChanges || isSaving}>
                    Discard changes
                  </Button>
                  <Button type="primary" onClick={() => setShowConfirmModal(true)} disabled={!hasChanges || isLoadingList || Boolean(listError)}>
                    Save changes
                  </Button>
                </Flex>
              </Flex>
            </>
          )}
        </>
      )}
      <Modal
        cancelText="Cancel"
        title={`Save changes to "${selectedSegment?.name ?? "segment"}"?`}
        content={`${describeChanges(type, unsavedAdds.length, unsavedRemoves.length)}. After saving, the segment targets ${countOf(
          type,
          resultingCount
        )}. Flags using it change immediately.`}
        onCancel={() => setShowConfirmModal(false)}
        onOk={handleSave}
        okText="Save changes"
        showModal={showConfirmModal && Boolean(selectedSegment)}
        confirmLoading={isSaving}
      />
    </MainPage>
  );
};

// Kept apart from the app's persisted cache, so segment members' names and emails stay in memory and never reach localStorage.
const segmentQueryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

export const FeatBitSegments = () => (
  <QueryClientProvider client={segmentQueryClient}>
    <FeatBitSegmentsEditor />
  </QueryClientProvider>
);
